import { type ComponentPropsWithRef } from 'react';
import { type ImageFormat, type ImageTransform } from '@sr/types/content/image';
import { getSrcSet, getVariantUrl } from './imageUrls';
import { resolveImageSizes, type ImageSize } from './resolveImageSizes';

type ImageQuality = Partial<Record<ImageFormat, ImageTransform['quality']>>;

export type ImageProps = Omit<ComponentPropsWithRef<'img'>, 'src' | 'srcSet' | 'alt' | 'sizes'> & {
  src: string;
  alt: string;
  variants: readonly ImageSize[];
  sizes: string;
  fallbackSize?: ImageSize;
  fallbackFormat?: Extract<ImageFormat, 'jpeg' | 'png'>;
  fit?: ImageTransform['fit'];
  quality?: ImageQuality;
};

/**
 * Responsive image with AVIF/WebP sources and a JPEG or PNG fallback.
 * Explicit variants define requested dimensions; sizes describes the rendered width.
 * Native width and height props affect layout only.
 *
 * @param props - Image options and native img props.
 * @param props.src - Absolute optimization endpoint URL with the source key, without width/height/fit parameters.
 * @param props.alt - Alternative text; use an empty string for decorative images.
 * @param props.variants - Explicit srcSet widths or width/height pairs, in pixels.
 * @param props.sizes - Rendered image width, e.g. "56px" or "(max-width: 640px) 100vw, 640px".
 * @param props.fallbackSize - Fallback src dimensions; defaults to the first variant.
 * @param props.fallbackFormat - Defaults to JPEG. Use PNG to preserve transparency.
 * @param props.fit - Resize fit mode; the API defaults to inside.
 * @param props.quality - Quality overrides (1-100); defaults to 80 for every format. PNG quality enables palette quantization.
 *
 * @example
 * <Image
 *   src={imageUrl}
 *   alt="Recycling container"
 *   variants={[320, 640, 960]}
 *   sizes="(max-width: 640px) 100vw, 640px"
 *   fallbackSize={640}
 * />
 */
export const Image = ({
  src,
  alt,
  variants,
  fallbackSize,
  fallbackFormat = 'jpeg',
  fit,
  quality = {},
  sizes,
  width,
  height,
  loading = 'lazy',
  decoding = 'async',
  ...props
}: ImageProps) => {
  const { resolvedVariants, resolvedFallbackSize } = resolveImageSizes({
    variants,
    fallbackSize,
  });
  const defaultQuality = 80;

  const fallbackQuality = quality[fallbackFormat] ?? defaultQuality;

  return (
    <picture>
      <source
        type="image/avif"
        sizes={sizes}
        srcSet={getSrcSet(src, resolvedVariants, 'avif', quality.avif ?? defaultQuality, fit)}
      />
      <source
        type="image/webp"
        sizes={sizes}
        srcSet={getSrcSet(src, resolvedVariants, 'webp', quality.webp ?? defaultQuality, fit)}
      />
      <img
        {...props}
        alt={alt}
        sizes={sizes}
        width={width}
        height={height}
        loading={loading}
        decoding={decoding}
        src={getVariantUrl(src, resolvedFallbackSize, fallbackFormat, fallbackQuality, fit)}
        srcSet={getSrcSet(src, resolvedVariants, fallbackFormat, fallbackQuality, fit)}
      />
    </picture>
  );
};
